import { useApolloClient } from '@apollo/client';
import { Cancel, Save } from '@mui/icons-material';
import { Grid, Tooltip } from '@mui/material';

import { Formik } from 'formik';
import { Gateway } from 'index';
import { omit } from 'lodash';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import TEST_TAXID_QUERY from 'gql/queries/testTaxId.gql';

import { useCompaniesMutation } from 'api/hooks/jobmarket/companies';
import { useJobmarketFacultiesQuery } from 'api/hooks/useJobmarketFacultiesQuery';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import StyledDropzone from 'app/components/Dropzone';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { SubtleActionButton } from 'app/components/SubtleActionButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { actions as FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { Faculty } from 'types/AppTypes';
import { euCountries } from 'utils/Countries';
import { validateForm } from 'utils/FormErrorHelper';

import CompanyDetailBody from './components/CompanyDetailBody';
import { companyValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
}

//TODO: Move this to a types package from gateway / external vat checker service
export interface VatCheckResult {
  externalVatCheckApplied: boolean;
  country_code: string;
  vat_number: string;
  format_valid: boolean;
  message: string;
}

export function CreateCompany(props: Props) {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const client = useApolloClient();

  const { create } = useCompaniesMutation();

  const { data: faculties } = useJobmarketFacultiesQuery();

  const data: { faculties: Faculty[]; company: any } = {
    faculties: faculties,
    company: {
      mainContact: {
        firstName: 'Change',
        lastName: 'This',
        email: 'change@me.de',
        phone: '000',
        position: 'TBD'
      },
      courses: [],
      description: '',
      sameInvoiceaddress: false,
      invoiceName: '',
      invoiceStreet: '',
      invoiceZip: '',
      invoiceCity: '',
      invoiceCountry: '',
      taxId: '',
      taxNumber: '',
      blockReason: '',
      city: '',
      country: '',
      email: '',
      fullAddress: '',
      homepage: '',
      name: ''
    }
  };

  const handleCancelClick = dirty => {
    if (dirty) {
      setCancelModalOpen(true);
    } else {
      props.closeHandler();
    }
  };

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    let saveBody: any = {
      ...omit(values, ['__typename', 'id', 'createdAt', 'active', 'state', 'logo']),
      courses: values.courses.map(c => c.id)
    };
    if (values.sameInvoiceaddress) {
      saveBody = omit(saveBody, [
        'invoiceName',
        'invoiceStreet',
        'invoiceCity',
        'invoiceZip',
        'invoiceCountry'
      ]);
    }

    if (values.logo) {
      // Save logo first
      const formData = new FormData();
      formData.append('file', values.logo);
      const { data: fileURI } = await Gateway.post('/upload/logo', formData);
      saveBody.logo = fileURI;
    }

    if (values.country !== 'Germany' && euCountries.includes(values.country)) {
      dispatch(AlertActions.success(t(translations.common.validatingTaxId())));
      let dataTestTax: VatCheckResult | null = null;
      try {
        const response = await client.query({
          query: TEST_TAXID_QUERY,
          variables: { taxIdForeignCompany: values.taxId }
        });
        dataTestTax = response.data?.testTaxId as VatCheckResult;
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.common.failedValidatingTaxId(), {
              cause: 'Failed fetching'
            })
          )
        );
        setSubmitting(false);
        return;
      }
      if (dataTestTax) {
        if (!dataTestTax.format_valid) {
          dispatch(
            AlertActions.error(
              t(translations.common.failedValidatingTaxId(), {
                cause: dataTestTax.message
              })
            )
          );
          setSubmitting(false);
          return;
        }
      }
    }

    try {
      await create.mutateAsync(saveBody);
      props.closeHandler();
      dispatch(AlertActions.success(t(translations.pages.manageCompanies.alerts.saveSuccessful())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.manageCompanies.alerts.saveFailed())));
    }
    setSubmitting(false);
  };

  const renderDetailView = (
    { setFieldValue, values, errors },
    dirty?: boolean,
    isValid?: boolean
  ) => {
    return (
      <div className="company-detail">
        <Helmet>
          <title>{t(translations.pages.manageCompanies.createCompany())}</title>
        </Helmet>
        <Grid
          className="company-detail__header"
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={2}>
            <StyledDropzone
              name="logo"
              setFieldValue={setFieldValue}
              value={values.logo}
              errors={errors}
              accept={{
                'image/jpeg': ['.jfif', '.jfif-tbnl', '.jpe', '.jpeg', '.jpg'],
                'image/png': ['.png', '.x-png'],
                'image/svg+xml': ['.svg']
              }}
              placeholder={t(translations.pages.manageCompanies.logoUploadPlaceholder())}
            />
          </Grid>
          <Grid item xs={10} style={{ textAlign: 'center' }}>
            <Tooltip
              title={t(translations.validation.formHasErrors())}
              disableHoverListener={isValid}
              disableFocusListener={isValid}
              disableTouchListener={isValid}
            >
              <span>
                <SubtleActionButton
                  slide
                  type="positive"
                  icon={Save}
                  submit
                  disabled={!isValid || submitting}
                  loading={submitting}
                >
                  {t(translations.common.save())}
                </SubtleActionButton>
              </span>
            </Tooltip>
            <SubtleActionButton
              slide
              type="neutral"
              icon={Cancel}
              disabled={submitting}
              onClick={() => {
                handleCancelClick(dirty);
              }}
            >
              {t(translations.common.cancel())}
            </SubtleActionButton>
          </Grid>
        </Grid>
        <CompanyDetailBody company={data.company} faculties={data.faculties} editMode newCompany />
        <ConfirmationDialog
          open={cancelModalOpen}
          title={t(translations.common.confirmCancel())}
          onClose={() => {
            setCancelModalOpen(false);
          }}
          onConfirm={() => {
            setCancelModalOpen(false);
            props.closeHandler();
            dispatch(FormErrorActions.close());
          }}
        >
          <div>{t(translations.pages.manageCompanies.cancelConfirm.message())}</div>
        </ConfirmationDialog>
      </div>
    );
  };

  return (
    <ErrorBoundary boundaryTag="createCompanyBoundary">
      <Formik
        initialValues={{ ...data.company }}
        isInitialValid={false}
        onSubmit={values => {
          handleSaveClick(values);
        }}
        validate={values => {
          return validateForm(values, companyValidationSchema(t), dispatch);
        }}
      >
        {({ dirty, handleSubmit, setFieldValue, values, errors, isValid }) => {
          return (
            <form onSubmit={handleSubmit}>
              {renderDetailView({ setFieldValue, values, errors }, dirty, isValid)}
            </form>
          );
        }}
      </Formik>
    </ErrorBoundary>
  );
}
